import React, { useState } from "react";
import { Box, Button, Collapse, Tooltip, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { CollectionVariant } from "../../../api/derin";
import {
  chipSx,
  collectionColors,
  font,
  formatDate,
  formatDateTime,
  inputSx,
  outlinedButtonSx,
  solidChipSx,
  subtitleSx,
} from "./tokens";

interface VariantRowProps {
  variant: CollectionVariant;
  historyOpen: boolean;
  onToggleHistory: () => void;
  onRename: (label: string) => void;
  onUploadVersion: () => void;
  onSetPrimary: () => void;
  onRemove: () => void;
}

const VariantRow: React.FC<VariantRowProps> = ({
  variant,
  historyOpen,
  onToggleHistory,
  onRename,
  onUploadVersion,
  onSetPrimary,
  onRemove,
}) => {
  const [editingLabel, setEditingLabel] = useState(false);
  const [draftLabel, setDraftLabel] = useState(variant.label);

  const startEditing = () => {
    setDraftLabel(variant.label);
    setEditingLabel(true);
  };

  const commitLabel = () => {
    const trimmed = draftLabel.trim();
    setEditingLabel(false);
    if (trimmed && trimmed !== variant.label) {
      onRename(trimmed);
    }
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        border: `1px solid ${collectionColors.border}`,
        borderRadius: "10px",
        background: collectionColors.white,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "12px",
        }}
      >
        {/* Left — icon, label, chips, metadata */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              flexShrink: 0,
              background: collectionColors.surfaceLight,
              borderRadius: "8px",
            }}
          >
            <DescriptionOutlinedIcon sx={{ width: 16, height: 16, color: collectionColors.textSecondary }} />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              {editingLabel ? (
                <Box
                  component="input"
                  autoFocus
                  value={draftLabel}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraftLabel(event.target.value)}
                  onBlur={commitLabel}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === "Enter") {
                      commitLabel();
                    }
                    if (event.key === "Escape") {
                      setEditingLabel(false);
                    }
                  }}
                  sx={{ ...inputSx, width: "180px", height: "28px" }}
                />
              ) : (
                <>
                  <Typography
                    sx={{
                      fontFamily: font,
                      fontWeight: 500,
                      fontSize: "15px",
                      lineHeight: "22px",
                      color: collectionColors.textPrimary,
                    }}
                  >
                    {variant.label}
                  </Typography>
                  <Tooltip title="Rename label">
                    <Box
                      component="button"
                      type="button"
                      aria-label="Rename label"
                      onClick={startEditing}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "20px",
                        height: "20px",
                        padding: 0,
                        background: "transparent",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        "&:hover": { background: "rgba(0, 0, 0, 0.05)" },
                      }}
                    >
                      <EditOutlinedIcon sx={{ width: 14, height: 14, color: collectionColors.textSecondary }} />
                    </Box>
                  </Tooltip>
                </>
              )}

              {variant.isPrimary && <Box sx={solidChipSx}>Primary variant</Box>}
              {variant.version && <Box sx={chipSx}>v{variant.version}</Box>}
            </Box>

            <Typography
              sx={{
                ...subtitleSx,
                fontSize: "13px",
                lineHeight: "18px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {variant.surveyOriginalId}
            </Typography>
            <Typography sx={{ ...subtitleSx, fontSize: "13px", lineHeight: "18px" }}>
              Uploaded {formatDate(variant.uploadedAt)}
              {variant.transcriptCount > 0 &&
                ` · ${variant.transcriptCount} response${variant.transcriptCount === 1 ? "" : "s"}`}
            </Typography>
          </Box>
        </Box>

        {/* Right — actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <Button
            startIcon={<FileUploadOutlinedIcon sx={{ width: 16, height: 16 }} />}
            onClick={onUploadVersion}
            sx={outlinedButtonSx}
          >
            Upload new version
          </Button>

          <Tooltip title={historyOpen ? "Hide version history" : "Show version history"}>
            <Box
              onClick={onToggleHistory}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px",
                cursor: "pointer",
                borderRadius: "6px",
                "&:hover": { background: "rgba(0, 0, 0, 0.04)" },
              }}
            >
              <HistoryOutlinedIcon sx={{ width: 18, height: 18, color: collectionColors.textSecondary }} />
              {historyOpen ? (
                <KeyboardArrowUpIcon sx={{ width: 18, height: 18, color: collectionColors.textSecondary }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ width: 18, height: 18, color: collectionColors.textSecondary }} />
              )}
            </Box>
          </Tooltip>

          {/* Reassigning the primary is what unblocks removing the current one, so the
              action lives right next to the disabled delete that asks for it. */}
          {!variant.isPrimary && (
            <Tooltip title="Make this the primary variant">
              <Box
                component="button"
                type="button"
                aria-label="Set as primary variant"
                onClick={onSetPrimary}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  background: "transparent",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  "&:hover": { background: "rgba(0, 0, 0, 0.04)" },
                }}
              >
                <StarOutlineIcon
                  sx={{ width: 18, height: 18, color: collectionColors.textSecondary }}
                />
              </Box>
            </Tooltip>
          )}

          <Tooltip
            title={
              variant.isPrimary
                ? "Make another variant primary before removing this one"
                : "Remove variant from this collection"
            }
          >
            <Box
              component="button"
              type="button"
              aria-label="Remove variant"
              disabled={variant.isPrimary}
              onClick={onRemove}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                padding: 0,
                background: "transparent",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                "&:hover": { background: collectionColors.dangerSurface },
                "&:disabled": { opacity: 0.35, cursor: "default", background: "transparent" },
              }}
            >
              <DeleteOutlineOutlinedIcon sx={{ width: 18, height: 18, color: collectionColors.textSecondary }} />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Version history */}
      <Collapse in={historyOpen}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            padding: "12px",
            borderTop: `1px solid ${collectionColors.border}`,
          }}
        >
          {variant.versionHistory.length === 0 ? (
            <Typography sx={subtitleSx}>No versions found for this variant.</Typography>
          ) : (
            variant.versionHistory.map((version) => (
              <Box
                key={version.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px 12px",
                  background: collectionColors.surfaceLight,
                  borderRadius: "8px",
                }}
              >
                <Box sx={version.isCurrent ? solidChipSx : chipSx}>v{version.version}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: font,
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: collectionColors.textPrimary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {version.title}.yaml
                  </Typography>
                  <Typography sx={{ ...subtitleSx, fontSize: "12px", lineHeight: "16px" }}>
                    {formatDateTime(version.updatedAt)}
                  </Typography>
                </Box>
                {version.isCurrent && <Box sx={chipSx}>Current</Box>}
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default VariantRow;
